import {
  BlockedRecordContentType,
  useGetUploadSignedUrlsForBlockedRecordLazyQuery,
} from '@exam-center/graphql';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

interface UseUploadBlockedRecordFilesInput {
  blockedRecordId?: string;
}

export default function useUploadBlockedRecordFiles(
  params?: Readonly<UseUploadBlockedRecordFilesInput>,
) {
  const { blockedRecordId = null } = params || {};

  const { showSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);

  const [getUploadSignedUrls, { loading: isUploadSignedUrlLoading }] =
    useGetUploadSignedUrlsForBlockedRecordLazyQuery();

  const handleUpload = async (files: File[]) => {
    if (!files.length) {
      return [];
    }

    const contentTypes = files.map<BlockedRecordContentType>((file) => {
      switch (file.type) {
        case 'application/pdf':
          return BlockedRecordContentType.Pdf;
        case 'image/jpeg':
          return BlockedRecordContentType.Jpeg;
        case 'image/png':
          return BlockedRecordContentType.Png;
        default:
          throw new Error(`Unsupported file type: ${file.type}`);
      }
    });

    const { data: getUploadSignedUrlsResult } = await getUploadSignedUrls({
      variables: {
        getUploadSignedUrlsForBlockedRecordInput: {
          blockedRecordId,
          contentTypes,
        },
      },
      fetchPolicy: 'no-cache',
    });

    if (!getUploadSignedUrlsResult?.ecGetUploadSignedUrlsForBlockedRecord.signedUrls) {
      throw new Error('Failed to get upload signed URLs.');
    }

    if (
      getUploadSignedUrlsResult.ecGetUploadSignedUrlsForBlockedRecord.signedUrls.some(
        (url) => !url.storageObject?.fileName,
      )
    ) {
      throw new Error('Some Upload File Names are missing.');
    }

    setIsLoading(true);

    const signedUrlData =
      getUploadSignedUrlsResult.ecGetUploadSignedUrlsForBlockedRecord.signedUrls;

    try {
      const uploadPromises = files.map(async (file, index) => {
        const {
          url: uploadSignedUrl,
          storageObject: { fileName: uploadFileName },
        } = signedUrlData[index];

        const uploadResponse = await fetch(uploadSignedUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload file: ${file.name}`);
        }

        return {
          originalFileName: file.name,
          uploadFileName: uploadFileName as string,
        };
      });

      return await Promise.all(uploadPromises);
    } catch (error) {
      showSnackbar({ variant: 'error', message: 'เกิดข้อผิดพลาดในการอัปโหลดไฟล์' });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { handleUpload, loading: isUploadSignedUrlLoading || isLoading };
}
